import { RFIDAssetsRow } from "./type";
import { Link } from "react-router-dom";
import { Badge } from "../../../components/Badge";
import { ColDef, ICellRendererParams } from "ag-grid-community";
import React, { useState } from "react";
import PromoCodeSendModal from "../../../components/PromoCodeSendModal";
import toast from "react-hot-toast";
import { createApolloClient } from "../../../providers/ApolloClientFactory";
import { TICKET_SEND_EMAIL_SMS } from "../ticket-search/mutations";
import {downloadBrandedQR} from "./brandedQr";
import {printBrandedQR} from "./printBrandedQr";
import {BWLogoBase64, ColorLogoBase64} from "../../../constants";
import { useEventStore } from "../../../store/event";
import { useUserStore } from "../../../store/user";

const client = createApolloClient();

const ButtonCellRenderer = (props: any) => {
    const [isModalOpen, setIsModalOpen] = useState(false);
    const [type, setType] = useState("");
    const { data } = props as { data: RFIDAssetsRow };
    const { id, uid } = data || {};
    const tokensBalance = data?.tokens_balance;
    let tokenStrings: string[];
    let tokenBalanceString = '';

    if (tokensBalance && Object.keys(tokensBalance).length > 0) {
        tokenStrings = Object.values<any>(tokensBalance).map(
            (token) => `${token.redeemable_token_name} - ${token.balance}`
        );
        tokenBalanceString = tokenStrings.join(", ");
    }

    const { user } = useUserStore();


    const username = user?.username || user?.email || "";

    const handleButtonClick = (sendType: string) => {
        setType(sendType);
        setIsModalOpen(true);
    };

    const handleDownloadClick = async (e: React.MouseEvent<HTMLButtonElement>) => {
        e.stopPropagation();
        try {
            const accessType =
                (data as any)?.access_type || (data?.is_qr ? "QR Code" : "Asset");

            await downloadBrandedQR({
                uid: String(uid ?? ""),
                itemName: accessType,
                bottomText: `UID: ${uid}`,
                bottomText2: data.reg_id ? `Print ID: ${data?.reg_id}` : '',
                bottomText3: tokenStrings ? `Token Balance: ${tokenStrings}` : '',
                logoBase64: ColorLogoBase64,
                qrSize: 500,
                logoSize: 100,
                fontSize: 24,
                fileName: `${String(accessType).replace(/\s+/g, "-")}-${uid}`,
            });

            toast.success("QR downloaded");
        } catch (error) {
            toast.error("Failed to download QR");
        }
    };

    const handlePrintClick = async (e: React.MouseEvent<HTMLButtonElement>) => {
        e.stopPropagation();
        try {


            await printBrandedQR({
                uid: String(uid ?? ""),
                eventName: data.event.name,
                accessType: data.access_type,
                tokenBalance: tokenBalanceString,
                printedBy: username,
                logoBase64: BWLogoBase64,
            });

            toast.success("Print dialog opened");
        } catch (error) {
            toast.error("Failed to print QR");
        }
    };

    return (
        <>
            <PromoCodeSendModal
                isOpen={isModalOpen}
                title={"Send Ticket via "}
                onClose={() => setIsModalOpen(false)}
                type={type}
                onConfirm={async ({
                                      type,
                                      value,
                                      setOpen,
                                      setEmail,
                                      setPhoneNumber,
                                      setType,
                                  }) => {
                    try {
                        await client.mutate({
                            mutation: TICKET_SEND_EMAIL_SMS,
                            variables: {
                                data: {
                                    uid: uid,
                                    methodType: "qr_code",
                                    email: type === "mail" ? value : undefined,
                                    phone_number: type !== "mail" ? value : undefined,
                                    id: `${id}`,
                                    type,
                                },
                            },
                        });

                        toast.success("Ticket QR Code Sent Successfully");
                        setOpen(false);
                        setEmail("");
                        setPhoneNumber("");
                        setType("");
                    } catch (error) {
                        toast.error("Error Sending Ticket QR Code: " + error);
                    }
                }}
            />

            <button
                onClick={(e) => {
                    e.stopPropagation();
                    handleButtonClick("mail");
                }}
                className="rounded-md border border-gray-300 bg-gray-400 py-1 px-2 text-sm font-medium text-white shadow-sm hover:bg-primary-700 focus:outline-none focus:ring-2 focus:ring-primary-500 focus:ring-offset-2"
            >
                Send Email
            </button>

            <button
                onClick={(e) => {
                    e.stopPropagation();
                    handleButtonClick("sms");
                }}
                className="ml-2 rounded-md border border-gray-300 bg-gray-400 py-1 px-2 text-sm font-medium text-white shadow-sm hover:bg-primary-700 focus:outline-none focus:ring-2 focus:ring-primary-500 focus:ring-offset-2"
            >
                Send SMS
            </button>

            {/* ⬇️ New: Download button */}
            <button
                onClick={handleDownloadClick}
                className="ml-2 rounded-md border border-gray-300 bg-gray-400 py-1 px-2 text-sm font-medium text-white shadow-sm hover:bg-primary-700 focus:outline-none focus:ring-2 focus:ring-primary-500 focus:ring-offset-2"
                title="Download QR"
            >
                Download QR
            </button>

            {/* ⬇️ New: Print button */}
            <button
                onClick={handlePrintClick}
                className="ml-2 rounded-md border border-gray-300 bg-gray-400 py-1 px-2 text-sm font-medium text-white shadow-sm hover:bg-primary-700 focus:outline-none focus:ring-2 focus:ring-primary-500 focus:ring-offset-2"
                title="Print QR"
            >
                Print Ticket
            </button>
        </>
    );
};

type CellRenderer = (params: {
    value:
        | string
        | number
        | boolean
        | React.ReactElement<any, string | React.JSXElementConstructor<any>>
        | React.ReactFragment
        | React.ReactPortal
        | null
        | undefined;
    data: RFIDAssetsRow;
}) => JSX.Element;

const createCellRenderer = (
    permission: boolean,
    linkFunc: (params: { data: RFIDAssetsRow }) => string
): CellRenderer => {
    return (params) => (
        <>
            {permission ? (
                <Link to={linkFunc(params)}>
                    <Badge>{params.value}</Badge>
                </Link>
            ) : (
                <Badge>{params.value}</Badge>
            )}
        </>
    );
};

const generateColDefs = (): ColDef<RFIDAssetsRow>[] => [
    {
        headerName: "UID",
        valueGetter: (p) => p.data?.uid,
        cellRenderer: createCellRenderer(true, (p) => `/rfid-assets/${p.data.id}/edit`),
    },
    {
        headerName: "Type",
        valueGetter: (p) => p.data?.is_qr,
        valueFormatter: (p) => {
            return p.data?.is_qr ? "QR" : "RFID";
        },
    },
    {
        headerName: "Token Balance",
        valueGetter: (params) => {
            const tokensBalance = params?.data?.tokens_balance;
            if (tokensBalance && Object.keys(tokensBalance).length > 0) {
                const tokenStrings = Object.values<any>(tokensBalance).map(
                    (token) => `${token.redeemable_token_name} - ${token.balance}`
                );
                return tokenStrings.join(", ");
            }
            return null;
        },
        cellRenderer: (params: ICellRendererParams) => {
            const tokensBalance = params.value;
            if (tokensBalance) {
                return <div>{tokensBalance}</div>;
            }
            return null;
        },
    },
    {
        headerName: "Is Active?",
        valueGetter: (p) => p.data?.is_active,
        valueFormatter: (p) => {
            return p.data?.is_active ? "Yes" : "No";
        },
    },
    {
        headerName: "Actions",
        cellRenderer: ButtonCellRenderer,
        minWidth: 425,
    },
];

export const rfidColDefs: ColDef<RFIDAssetsRow>[] = generateColDefs();
